import {
    Dialog,
    DialogContent, 
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'   
import { TypographyMuted, TypographyXS } from '@/components/ui/typography'
import { ProductsNotSearched } from '@/types/MarketResearch'
import { useResearchCoverageProductsNotSearchedQuery } from '@/queries/useResearchCoverageProductsNotSearched'
import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Loader2, Package, Store, User } from 'lucide-react'
import Button from '@/components/Button'
import { Input } from '@/components/ui/input'

interface ProductsNotSearchedDialogProps {
    isOpen: boolean
    onClose: () => void
    skPesquisa: number
}

const ITEMS_PER_PAGE = 1000

const ProductsNotSearchedDialog = ({ isOpen, onClose, skPesquisa }: ProductsNotSearchedDialogProps) => {    
    const [currentPage, setCurrentPage] = useState(1)
    const [searchTerm, setSearchTerm] = useState('')

    const { data, isLoading } = useResearchCoverageProductsNotSearchedQuery(skPesquisa)

    const filteredData = useMemo(() => {
        if (!data) return []
        
        if (!searchTerm.trim()) return data

        const lowerSearch = searchTerm.toLowerCase()
        return data.filter((item: ProductsNotSearched) => 
            item.DS_PRODUTO?.toLowerCase().includes(lowerSearch) ||
            item.DS_NOME_FANTASIA?.toLowerCase().includes(lowerSearch) ||
            item.DS_REVENDA?.toLowerCase().includes(lowerSearch)
        )
    }, [data, searchTerm])

    const totalPages = Math.ceil((filteredData?.length || 0) / ITEMS_PER_PAGE)
    
    const paginatedData = useMemo(() => {
        if (!filteredData) return []
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
        const endIndex = startIndex + ITEMS_PER_PAGE
        return filteredData.slice(startIndex, endIndex)
    }, [filteredData, currentPage])

    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1))
    }

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages))
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
        setCurrentPage(1)
    }

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setCurrentPage(1)
            setSearchTerm('')
        }
        onClose()
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className='max-w-[900px] max-h-[90vh] flex flex-col' aria-describedby={undefined}>
                <DialogHeader> 
                    <DialogTitle>Produtos não pesquisados</DialogTitle>
                    <TypographyMuted>
                        Lista de produtos que ainda não foram pesquisados pelos clientes
                    </TypographyMuted>
                </DialogHeader> 

                <div className="mb-4">
                    <Input
                        placeholder="Buscar por produto, cliente ou revenda..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="w-full"
                    />
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="ml-2">Carregando produtos...</span>
                    </div>
                ) : (
                    <>
                        {filteredData && filteredData.length > 0 ? (
                            <>
                                <div className="flex-1 overflow-auto">
                                    <div className="space-y-2">
                                        {paginatedData.map((item: ProductsNotSearched, index: number) => (
                                            <div 
                                                key={index} 
                                                className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Store size={14} className="text-gray-500 shrink-0" />
                                                    <div className="min-w-0">
                                                        <TypographyXS className="text-xs text-gray-500">Revenda</TypographyXS>
                                                        <p className="text-sm font-medium text-gray-900 truncate" title={item.DS_REVENDA}>
                                                            {item.DS_REVENDA}
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-2">
                                                    <User size={14} className="text-gray-500 shrink-0" />
                                                    <div className="min-w-0">
                                                        <TypographyXS className="text-xs text-gray-500">Cliente</TypographyXS>
                                                        <p className="text-sm font-medium text-gray-900 truncate" title={item.DS_NOME_FANTASIA}>
                                                            {item.DS_NOME_FANTASIA}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Package size={14} className="text-gray-500 shrink-0" />
                                                    <div className="min-w-0">
                                                        <TypographyXS className="text-xs text-gray-500">Produto</TypographyXS>
                                                        <p className="text-sm font-medium text-gray-900 truncate" title={item.DS_PRODUTO}>
                                                            {item.DS_PRODUTO}
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between pt-4 border-t mt-4">
                                    <TypographyXS className="text-gray-500">
                                        Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length)} de {filteredData.length} produtos
                                    </TypographyXS>
                                    
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                        >
                                            <ChevronLeft size={16} />
                                            Anterior
                                        </Button>
                                        
                                        <span className="px-3 py-1 text-sm font-medium">
                                            {currentPage} / {totalPages}
                                        </span>
                                        
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleNextPage}
                                            disabled={currentPage === totalPages}
                                        >
                                            Próximo
                                            <ChevronRight size={16} />
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <Package size={48} className="mb-4 opacity-50" />
                                <p className="text-lg font-medium">Nenhum produto encontrado</p>
                                <TypographyMuted>
                                    {searchTerm ? 'Tente ajustar os termos da busca' : 'Todos os produtos já foram pesquisados'}
                                </TypographyMuted>
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default ProductsNotSearchedDialog
